import { Box } from '@mui/material';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { SeverityLevel } from '../../../../Component/Model/Enum/EmploymentEquity';
import colors from '../../../../assets/theme/base/colors';

interface SeverityStatCardsProps {
    statistics: Record<SeverityLevel, number>;
}

const statCardConfig = [
    {
        severity:  SeverityLevel.Critical,
        label:     'Critical Severity',
        threshold: 'below -25%',
        bg:          colors.deviationSeverity.critical.bg,
        iconBg:      colors.deviationSeverity.critical.iconBg,
        iconColor:   colors.deviationSeverity.critical.icon,
        Icon:        DisabledByDefaultOutlinedIcon,
    },
    {
        severity:  SeverityLevel.High,
        label:     'High Severity',
        threshold: '-15% to -25%',
        bg:          colors.deviationSeverity.high.bg,
        iconBg:      colors.deviationSeverity.high.iconBg,
        iconColor:   colors.deviationSeverity.high.icon,
        Icon:        WarningAmberOutlinedIcon,
    },
    {
        severity:  SeverityLevel.Medium,
        label:     'Medium Severity',
        threshold: '-5% to -15%',
        bg:          colors.deviationSeverity.medium.bg,
        iconBg:      colors.deviationSeverity.medium.iconBg,
        iconColor:   colors.deviationSeverity.medium.icon,
        Icon:        InfoOutlinedIcon,
    },
    {
        severity:  SeverityLevel.OnTrack,
        label:     'On Track',
        threshold: 'within -5% to 0%',
        bg:          colors.deviationSeverity.onTrack.bg,
        iconBg:      colors.deviationSeverity.onTrack.iconBg,
        iconColor:   colors.deviationSeverity.onTrack.icon,
        Icon:        CheckCircleOutlinedIcon,
    },
    {
        severity:  SeverityLevel.OverRepresented,
        label:     'Over-represented',
        threshold: 'above 0%',
        bg:          colors.deviationSeverity.overRepresented.bg,
        iconBg:      colors.deviationSeverity.overRepresented.iconBg,
        iconColor:   colors.deviationSeverity.overRepresented.icon,
        Icon:        TrendingUpIcon,
    },
];

export function SeverityStatCards({ statistics }: SeverityStatCardsProps) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, mb: 3 }}>
            {statCardConfig.map(({ severity, label, threshold, bg, iconBg, iconColor, Icon }) => (
                <Box
                    key={severity}
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        backgroundColor: bg,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        minHeight: 64,
                    }}
                >
                    <Box
                        sx={{
                            width: 48,
                            flexShrink: 0,
                            backgroundColor: iconBg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Icon sx={{ width: 22, height: 22, color: colors.white.main }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, flex: 1 }}>
                        <Box sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1, color: iconColor, flexShrink: 0 }}>
                            {statistics[severity] ?? 0}
                        </Box>
                        <Box>
                            <Box sx={{ fontSize: '0.75rem', fontWeight: 600, color: iconColor, lineHeight: 1.3 }}>
                                {label}
                            </Box>
                            <Box sx={{ fontSize: '0.625rem', color: iconColor, opacity: 0.75, lineHeight: 1.3 }}>
                                Relative gap:
                            </Box>
                            <Box sx={{ fontSize: '0.625rem', color: iconColor, opacity: 0.75, lineHeight: 1.3 }}>
                                {threshold}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}
